import type { WebhookAttempt } from "@/hooks/use-webhook-event-logs";

/** The three events, in the order a call sends them. */
export const EVENT_LABEL: Record<string, string> = {
  call_started: "Started",
  call_ended: "Ended",
  call_analyzed: "Analyzed",
};

/**
 * Attempts grouped by event: the three in send order, anything else after them.
 * Each group keeps the API's order, newest first.
 */
export const groupAttempts = (attempts: WebhookAttempt[]) =>
  [...new Set([...Object.keys(EVENT_LABEL), ...attempts.map((a) => a.event)])]
    .map((event) => ({ event, attempts: attempts.filter((a) => a.event === event) }))
    .filter((group) => group.attempts.length > 0);

/**
 * An attempt's result is recorded within the 12 s send timeout. One with none a
 * minute on never will: its sender died between committing the row and the POST's answer.
 */
const RESULT_DUE_MS = 60_000;

/**
 * The outcome badge. A 2xx, an HTTP error, a redirect and a 413 always carry the
 * status they were classified by; a timeout and a network error got no response.
 */
export function outcomeBadge({ outcome, response_status: code, started_at }: WebhookAttempt) {
  switch (outcome) {
    case "2xx":
      return { label: String(code), tone: "ok" as const };
    case "http_error":
      return { label: `HTTP ${code}`, tone: "fail" as const };
    case "redirect":
      return { label: `Redirect ${code}`, tone: "fail" as const };
    case "timeout":
      return { label: "Timeout", tone: "fail" as const };
    case "network":
      return { label: "Network error", tone: "fail" as const };
    case "too_large":
      // The endpoint's 413: it refused the body's size.
      return { label: `HTTP ${code} (too large)`, tone: "fail" as const };
    default:
      // `sending`: the POST is out, or its sender died before recording the answer.
      return Date.now() - Date.parse(started_at) > RESULT_DUE_MS
        ? { label: "No result recorded", tone: "muted" as const }
        : { label: "Sending", tone: "muted" as const };
  }
}

/**
 * A row's key, the same on every read: the attempt's id. A member gets no id, so
 * theirs is the event, the number and the time — `attempt_no` starts again at 1
 * when an event is sent again.
 */
export const attemptKey = (a: WebhookAttempt) => a.id ?? `${a.event}#${a.attempt_no}@${a.started_at}`;

/** JSON pretty-printed; anything else exactly as it came. */
export function pretty(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

export const formatDuration = (ms: number) => (ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
